import React from 'react';
import { theme } from '@weave/theme-original';
import {
  Heading,
  Text,
  PrimaryButton,
  SecondaryButton,
  TextField,
  TextareaField,
  DropdownField,
  ButtonBar,
  useForm,
} from '@weave/design-system';
import { useSelector } from 'react-redux';
import { AuthStorage } from '../../redux/actions/auth/auth.types';
import { Store } from '../../redux/store/store.model';
import {
  AnnouncementData,
  AnnouncementVersion,
  SimpleAnnouncementData,
  SimpleAnnouncementPayload,
} from './types';

// Reusable form component for both create and edit modes
export const AnnouncementForm = ({
  mode,
  announcementData: selectedAnnouncement,
  versionData,
  onCancel,
  onSave,
}: {
  mode: 'create' | 'edit';
  announcementData?: AnnouncementData;
  versionData?: AnnouncementVersion;
  onCancel: () => void;
  onSave: (data: {
    announcement: { id: string; isPublished: boolean };
    version: AnnouncementVersion;
  }) => void;
}) => {
  // Get current user's email
  const currentUserEmail =
    useSelector((state: Store) => state.auth.user.username) ||
    localStorage.getItem(AuthStorage.user_email) ||
    'Unknown User';

  // Generate a unique ID for new announcements
  const generateId = () => {
    return `ann-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Determine initial values based on mode
  const getInitialValues = () => {
    if (mode === 'create') {
      return {
        id: generateId(),
        version: '1',
        title: '',
        type: 'general',
        status: true,
        isPublished: false,
        message: '',
        actionHref: '',
        actionLabel: '',
        createdBy: currentUserEmail,
        priority: '1',
      };
    } else {
      // Edit mode - use existing data
      return {
        id: selectedAnnouncement?.id || '',
        version: selectedAnnouncement
          ? (selectedAnnouncement?.versions.length + 1).toString()
          : '1',
        title: versionData?.title || '',
        type: versionData?.type || 'general',
        status: false,
        isPublished: selectedAnnouncement?.isPublished || false,
        message: versionData?.message || '',
        actionHref: versionData?.action?.href || '',
        actionLabel: versionData?.action?.label || '',
        createdBy: currentUserEmail,
        priority: versionData?.priority?.toString() || '1',
      };
    }
  };

  const initialValues = getInitialValues();
  const { formProps, getFieldProps, values, isComplete, reset, seedValues } = useForm({
    fields: {
      id: { type: 'text', required: true, value: initialValues.id },
      version: { type: 'number', required: true, value: initialValues.version },
      title: { type: 'text', required: true, value: initialValues.title },
      type: { type: 'text', required: true, value: initialValues.type },
      status: { type: 'switch', value: initialValues.status },
      isPublished: { type: 'switch', value: initialValues.isPublished },
      message: { type: 'text', required: true, value: initialValues.message },
      actionHref: { type: 'text', value: initialValues.actionHref },
      actionLabel: { type: 'text', value: initialValues.actionLabel },
      createdBy: { type: 'text', value: initialValues.createdBy },
      priority: { type: 'number', value: initialValues.priority },
    },
    onSubmit: (formValues) => {
      const newVersion: AnnouncementVersion = {
        version: parseInt(formValues.version || '1'),
        title: formValues.title || '',
        type: formValues.type || 'general',
        message: formValues.message || '',
        action: formValues.actionHref
          ? {
              label: formValues.actionLabel || '',
              href: formValues.actionHref || '',
            }
          : undefined,
        priority: formValues.priority ? parseInt(formValues.priority) : 1,
        createdBy: currentUserEmail,
        createdOn: new Date().toISOString(),
      };

      onSave({
        announcement: {
          id: formValues.id || generateId(),
          isPublished: formValues.isPublished || false,
        },
        version: newVersion,
      });
    },
  });

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const typeOptions = [
    { label: 'Maintenance', value: 'maintenance' },
    { label: 'Feature', value: 'feature' },
    { label: 'Outage', value: 'outage' },
    { label: 'General', value: 'general' },
  ];

  const isCreate = mode === 'create';
  const title = isCreate ? 'Create New Announcement' : 'Create Revision';
  const formId = isCreate ? 'create-announcement-form' : 'edit-announcement-form';
  const submitText = isCreate ? 'Create Announcement' : 'Save Changes';

  return (
    <div
      style={{
        background: theme.colors.white,
        padding: theme.spacing(3),
        borderRadius: theme.borderRadius.medium,
        border: `1px solid ${theme.colors.gray300}`,
        display: 'grid',
        gap: theme.spacing(3),
        gridTemplateRows: 'auto 1fr',
        height: '100%',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          gap: theme.spacing(2),
        }}
      >
        <Heading css={{ margin: 0 }} level={3}>
          {title}
        </Heading>
      </div>

      <div
        css={{
          display: 'grid',
          gridTemplateRows: '1fr auto',
          gap: theme.spacing(2),
          height: '100%',
        }}
      >
        <form
          {...formProps}
          id={formId}
          css={{
            display: 'grid',
            gap: theme.spacing(2),
            gridAutoRows: 'max-content',
            overflowY: 'auto',
            paddingTop: theme.spacing(1),
          }}
        >
          <TextField {...getFieldProps('id')} label="ID" disabled />
          <TextField {...getFieldProps('version')} label="Version" disabled />
          <TextField {...getFieldProps('title')} label="Title" />
          <DropdownField {...getFieldProps('type')} label="Type">
            {typeOptions.map((option) => (
              <DropdownField.Option key={option.value} value={option.value}>
                {option.label}
              </DropdownField.Option>
            ))}
          </DropdownField>
          <TextField {...getFieldProps('priority')} label="Priority" />
          <TextareaField {...getFieldProps('message')} label="Message" />
          <div
            style={{
              borderRadius: theme.borderRadius.medium,
              border: `1px solid ${theme.colors.gray300}`,
              padding: theme.spacing(2),
              display: 'grid',
              gap: theme.spacing(2),
            }}
          >
            <Text
              as="div"
              size="medium"
              weight="bold"
              css={{ marginBottom: theme.spacing(1) }}
            >
              Action
            </Text>
            <TextField
              {...getFieldProps('actionLabel')}
              label="Label"
              placeholder="e.g., View Details"
            />
            <TextField
              {...getFieldProps('actionHref')}
              label="URL"
              placeholder="e.g., https://example.com/details"
            />
          </div>
          <TextField
            {...getFieldProps('createdBy')}
            value={currentUserEmail}
            label="Created by"
            disabled
          />
        </form>
        <ButtonBar>
          <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
          <PrimaryButton type="submit" form={formId} disabled={!isComplete}>
            {submitText}
          </PrimaryButton>
        </ButtonBar>
      </div>
    </div>
  );
};

export const SimpleAnnouncementForm = ({
  mode,
  announcementData: selectedAnnouncement,
  onCancel,
  onSave,
}: {
  mode: 'create' | 'edit';
  announcementData?: SimpleAnnouncementData;
  onCancel: () => void;
  onSave: (data: { announcement: Omit<SimpleAnnouncementData, 'history'> }) => void;
}) => {
  // Get current user's email
  const currentUserEmail =
    useSelector((state: Store) => state.auth.user.username) ||
    localStorage.getItem(AuthStorage.user_email) ||
    'Unknown User';

  // Generate a unique ID for new announcements
  const generateId = () => {
    return `ann-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Determine initial values based on mode
  const getInitialValues = () => {
    if (mode === 'create') {
      return {
        // id: generateId(),
        // version: '1',
        title: '',
        type: 'info',
        status: true,
        isPublished: false,
        message: '',
        path: '',
        actionHref: '',
        actionLabel: '',
        createdBy: currentUserEmail,
        priority: '1',
      };
    } else {
      // Edit mode - use existing data
      return {
        // id: selectedAnnouncement?.id || '',
        title: selectedAnnouncement?.title || '',
        type: selectedAnnouncement?.type || 'info',
        status: false,
        isPublished: selectedAnnouncement?.isPublished || false,
        message: selectedAnnouncement?.message || '',
        path: selectedAnnouncement?.path || '',
        actionHref: selectedAnnouncement?.action?.href || '',
        actionLabel: selectedAnnouncement?.action?.label || '',
        createdBy: currentUserEmail,
        priority: selectedAnnouncement?.priority?.toString() || '1',
      };
    }
  };

  const initialValues = getInitialValues();
  const { formProps, getFieldProps, values, isComplete, reset, seedValues } = useForm({
    fields: {
      title: { type: 'text', required: true, value: initialValues.title },
      type: { type: 'text', required: true, value: initialValues.type },
      status: { type: 'switch', value: initialValues.status },
      isPublished: { type: 'switch', value: initialValues.isPublished },
      message: { type: 'text', required: true, value: initialValues.message },
      path: { type: 'text', value: initialValues.path },
      actionHref: { type: 'text', value: initialValues.actionHref },
      actionLabel: { type: 'text', value: initialValues.actionLabel },
      createdBy: { type: 'text', value: initialValues.createdBy },
      priority: { type: 'number', value: initialValues.priority },
    },
    onSubmit: (formValues) => {
      onSave({
        announcement: {
          title: formValues.title || '',
          type: (formValues.type as 'info' | 'warning' | 'success' | 'error') || 'info',
          path: formValues.path || '',
          message: formValues.message || '',
          action: formValues.actionHref
            ? {
                label: formValues.actionLabel || '',
                href: formValues.actionHref || '',
              }
            : undefined,
          priority: formValues.priority ? parseInt(formValues.priority) : 1,
          // these are placeholders and not saved to the backend
          id: generateId(),
          isPublished: formValues.isPublished || false,
          createdBy: currentUserEmail,
          createdOn: new Date().toISOString(),
        },
      });
    },
  });

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const typeOptions = [
    { label: 'Error', value: 'error' },
    { label: 'Success', value: 'success' },
    { label: 'Warning', value: 'warning' },
    { label: 'Info', value: 'info' },
  ];

  const isCreate = mode === 'create';
  const title = isCreate ? 'Create New Announcement' : 'Create Revision';
  const formId = isCreate ? 'create-announcement-form' : 'edit-announcement-form';
  const submitText = isCreate ? 'Create Announcement' : 'Save Changes';

  return (
    <div
      style={{
        background: theme.colors.white,
        padding: theme.spacing(3),
        borderRadius: theme.borderRadius.medium,
        border: `1px solid ${theme.colors.gray300}`,
        display: 'grid',
        gap: theme.spacing(3),
        gridTemplateRows: 'auto 1fr',
        height: '100%',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          gap: theme.spacing(2),
        }}
      >
        <Heading css={{ margin: 0 }} level={3}>
          {title}
        </Heading>
      </div>

      <div
        css={{
          display: 'grid',
          gridTemplateRows: '1fr auto',
          gap: theme.spacing(2),
          height: '100%',
        }}
      >
        <form
          {...formProps}
          id={formId}
          css={{
            display: 'grid',
            gap: theme.spacing(2),
            gridAutoRows: 'max-content',
            overflowY: 'auto',
            paddingTop: theme.spacing(1),
          }}
        >
          {/* <TextField {...getFieldProps('id')} label="ID" disabled /> */}
          {/* <TextField {...getFieldProps('version')} label="Version" disabled /> */}
          <TextField {...getFieldProps('title')} label="Title" />
          <DropdownField {...getFieldProps('type')} label="Type">
            {typeOptions.map((option) => (
              <DropdownField.Option key={option.value} value={option.value}>
                {option.label}
              </DropdownField.Option>
            ))}
          </DropdownField>
          <TextField {...getFieldProps('priority')} label="Priority" />
          <TextField {...getFieldProps('path')} label="Path" />
          <TextareaField {...getFieldProps('message')} label="Message" />
          <Text size="small" color="subdued">
            Use **text** to bold, *text* to italicize
          </Text>
          <div
            style={{
              borderRadius: theme.borderRadius.medium,
              border: `1px solid ${theme.colors.gray300}`,
              padding: theme.spacing(2),
              display: 'grid',
              gap: theme.spacing(2),
            }}
          >
            <Text
              as="div"
              size="medium"
              weight="bold"
              css={{ marginBottom: theme.spacing(1) }}
            >
              Action
            </Text>
            <TextField
              {...getFieldProps('actionLabel')}
              label="Label"
              placeholder="e.g., View Details"
            />
            <TextField
              {...getFieldProps('actionHref')}
              label="URL"
              placeholder="e.g., https://example.com/details"
            />
          </div>
        </form>
        <ButtonBar>
          <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
          <PrimaryButton type="submit" form={formId} disabled={!isComplete}>
            {submitText}
          </PrimaryButton>
        </ButtonBar>
      </div>
    </div>
  );
};
